import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import VariablesService from "../../services/variablesService";

const handleApiError = (error, thunkAPI, defaultMsg, key = null) => {
  const apiError = error?.response?.data;
  let message = defaultMsg;

  if (apiError?.errors) {
    message = Object.values(apiError.errors).flat();
  } else if (apiError?.message) {
    message = apiError.message;
  }

  if (key) {
    return thunkAPI.rejectWithValue({ key, error: message });
  }

  return thunkAPI.rejectWithValue(Array.isArray(message) ? message : [message]);
};

export const fetchAllVariables = createAsyncThunk(
  "variable/fetchAllVariables",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await VariablesService.getAllVariables(params);
      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Fetch variables failed", key);
    }
  }
);

export const createVariable = createAsyncThunk(
  "variable/createVariable",
  async (data, thunkAPI) => {
    try {
      const res = await VariablesService.createVariable(data);
      return res.data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Create variable failed");
    }
  }
);

export const updateVariable = createAsyncThunk(
  "variable/updateVariable",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await VariablesService.updateVariable(id, data);
      return res.data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Update variable failed");
    }
  }
);

export const deleteVariable = createAsyncThunk(
  "variable/deleteVariable",
  async (id, thunkAPI) => {
    try {
      await VariablesService.deleteVariable(id);
      return id;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Delete variable failed");
    }
  }
);

export const fetchAllVariableTypes = createAsyncThunk(
  "formula/fetchAllVariableTypes",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await VariablesService.getAllVariableTypes(params);
      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Fetch variable types failed", key);
    }
  }
);

export const createVariableType = createAsyncThunk(
  "variableType/createVariableType",
  async (data, thunkAPI) => {
    try {
      const res = await VariablesService.createVariable(data);
      return res.data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Create variable failed");
    }
  }
);

export const updateVariableType = createAsyncThunk(
  "variableType/updateVariable",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await VariablesService.updateVariable(id, data);
      return res.data.data;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Update variable failed");
    }
  }
);

export const deleteVariableType = createAsyncThunk(
  "variableType/deleteVariable",
  async (id, thunkAPI) => {
    try {
      await VariablesService.deleteVariable(id);
      return id;
    } catch (e) {
      return handleApiError(e, thunkAPI, "Delete variable failed");
    }
  }
);

const variableSlice = createSlice({
  name: "variable",
  initialState: {
    listDataByKey: {},
    totalByKey: {},
    listLoadingByKey: {},
    entityByKey: {},
    entityLoadingByKey: {},
    entityErrorByKey: {},
    loading: false,
    isSuccess: false,
    errorMessage: null,
  },
  reducers: {
    resetStatus: (state) => {
      state.isSuccess = false;
      state.errorMessage = null;
      state.loading = false;
    },
    clearEntityError(state, action) {
      const key = action.payload;
      if (key) {
        state.entityErrorByKey[key] = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Delete
      .addCase(deleteVariable.fulfilled, (state, action) => {
        state.loading = false;
        state.isSuccess = true;
        Object.keys(state.listDataByKey).forEach((key) => {
          if (state.listDataByKey[key]) {
            state.listDataByKey[key] = state.listDataByKey[key].filter(
              (item) => item.id !== action.payload
            );
          }
        });
      })

      // Matchers for Key-based fetching
      .addMatcher(
        (action) => action.type.endsWith("/pending") && action.meta.arg?.key,
        (state, action) => {
          const key = action.meta.arg.key;
          state.listLoadingByKey[key] = true;
          state.entityLoadingByKey[key] = true;
          state.entityErrorByKey[key] = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/fulfilled") && action.payload?.key,
        (state, action) => {
          const { key, data, totalRecords } = action.payload;
          if (Array.isArray(data)) {
            state.listDataByKey[key] = data;
            state.totalByKey[key] = totalRecords || 0;
            state.listLoadingByKey[key] = false;
          } else {
            state.entityByKey[key] = data;
            state.entityLoadingByKey[key] = false;
          }
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected") && action.payload?.key,
        (state, action) => {
          const key = action.payload.key;
          state.listLoadingByKey[key] = false;
          state.entityLoadingByKey[key] = false;
          state.entityErrorByKey[key] = action.payload.error;
        }
      )

      // Matchers for Create/Update/Delete (Global loading)
      .addMatcher(
        (action) =>
          (action.type.includes("create") ||
            action.type.includes("update") ||
            action.type.includes("delete")) &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.isSuccess = false;
          state.errorMessage = null;
        }
      )
      .addMatcher(
        (action) =>
          (action.type.includes("create") || action.type.includes("update")) &&
          action.type.endsWith("/fulfilled"),
        (state) => {
          state.loading = false;
          state.isSuccess = true;
          state.errorMessage = null;
        }
      )
      .addMatcher(
        (action) =>
          (action.type.includes("create") ||
            action.type.includes("update") ||
            action.type.includes("delete")) &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.isSuccess = false;
          state.errorMessage = action.payload;
        }
      );
  },
});

export const { resetStatus, clearEntityError } = variableSlice.actions;
export default variableSlice.reducer;